import { useMemo } from 'react';
import { PageContainer, PageSectionTitle } from 'components/Layout/PageContainer';
import { PriceChange, TradingMessage } from 'pages/trading/model';
import { TradingMessageDisplay } from 'pages/trading/TradingMessageDisplay';
import { calculateDailyStats } from 'pages/trading/price/CalculateDailyStats';
import { PriceKPICard } from './PriceKPICard';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { ProductStyleBreakdownTable } from './ProductStyleBreakdownTable';
import { calcGrowth } from 'utils/calc';

export function PriceStandardDetailPage({ message, data }: { message: TradingMessage; data: PriceChange[] }) {
  const kpis = useMemo(() => {
    let totalOldGmv = 0,
      totalOldQty = 0;
    let totalNewGmv = 0,
      totalNewQty = 0;
    let weightedPriceDeltaSum = 0;
    let totalPriceDeltaWeight = 0;

    data.forEach(row => {
      const oldStats = calculateDailyStats(row.fromPrice);
      const newStats = calculateDailyStats(row.toPrice);
      totalOldGmv += oldStats.dailyGmv;
      totalOldQty += oldStats.dailyQty;
      totalNewGmv += newStats.dailyGmv;
      totalNewQty += newStats.dailyQty;
      const priceDelta = calcGrowth(newStats.price, oldStats.price) * 100;
      weightedPriceDeltaSum += priceDelta * oldStats.dailyQty;
      totalPriceDeltaWeight += oldStats.dailyQty;
    });

    return {
      dailyQty: totalNewQty,
      dailyQtyDelta: calcGrowth(totalNewQty, totalOldQty) * 100,
      dailyGmv: totalNewGmv,
      dailyGmvDelta: calcGrowth(totalNewGmv, totalOldGmv) * 100,
      avgPriceChange: totalPriceDeltaWeight > 0 ? weightedPriceDeltaSum / totalPriceDeltaWeight : 0,
    };
  }, [data]);

  return (
    <div className="standard-page-container">
      <PageContainer>
        <PageSectionTitle titleId="trading.price_analysis.title" />
        <TradingMessageDisplay message={message} />
        <PriceKPICard
          testid="kpi-standard-section"
          titleId="trading.price_change.kpi.avg_price_change"
          value={kpis.avgPriceChange / 100}
          unit={MeasureUnitCode.PERCENT}
          hidePill={true}
          subtextId="trading.price_change.kpi.subtext.avg_price_change"
        />
        <PriceKPICard
          testid="kpi-standard-section"
          titleId="trading.price_change.kpi.daily_items_new"
          value={kpis.dailyQty}
          unit={MeasureUnitCode.QUANTITY}
          delta={kpis.dailyQtyDelta}
          subtextId="trading.price_change.kpi.subtext.daily_items_old"
        />
        <PriceKPICard
          testid="kpi-standard-section"
          titleId="trading.price_change.kpi.daily_gmv_new"
          value={kpis.dailyGmv}
          unit={MeasureUnitCode.CURRENCY}
          delta={kpis.dailyGmvDelta}
          subtextId="trading.price_change.kpi.subtext.daily_gmv_old"
        />
        <ProductStyleBreakdownTable data={data} messageId={message.messageId} />
      </PageContainer>
    </div>
  );
}
